import { z } from 'zod';

import {
  isoDateTimeSchema,
  nonNegativeIntSchema,
  percentageSchema,
  positiveIntSchema,
  uuidSchema,
} from '../common/primitives';

/**
 * Die Zeitreihe der Node-Messwerte, verdichtet für die Anzeige.
 *
 * ## Warum verdichtet und nicht roh
 *
 * Der Master schreibt alle zehn Sekunden eine Zeile je Node und hält sie 90
 * Tage. Eine Woche sind damit rund 60 000 Punkte — mehr, als ein Diagramm
 * darstellen kann, und weit mehr, als ein Browser verarbeiten sollte. Der
 * Endpunkt fasst deshalb zu Eimern zusammen und nennt die Eimerbreite mit;
 * ohne sie ließe sich eine Lücke in den Daten nicht von einem stillen Node
 * unterscheiden.
 *
 * ## Warum der Mittelwert und nicht der Höchstwert
 *
 * Beides wäre vertretbar, aber nur eines darf es sein. Der Mittelwert zeigt
 * die Grundlast, um die es bei der Kapazitätsplanung geht. Für Spitzen gibt es
 * `cpuPercentMax` daneben — eine Zahl, die im selben Eimer eine ganz andere
 * Geschichte erzählen kann, und genau deshalb steht sie dabei.
 */

/** Ein Punkt der verdichteten Reihe. */
export const nodeMetricPointSchema = z.object({
  /** Beginn des Eimers. */
  at: isoDateTimeSchema,
  /** Wie viele Messungen in diesen Eimer gefallen sind — 0 gibt es nicht. */
  samples: positiveIntSchema,
  cpuPercent: percentageSchema,
  cpuPercentMax: percentageSchema,
  memoryUsedMb: nonNegativeIntSchema,
  memoryTotalMb: nonNegativeIntSchema,
  diskUsedGb: nonNegativeIntSchema,
  diskTotalGb: nonNegativeIntSchema,
  /** `null`, wenn kein Wert in diesem Eimer eine Load meldete. */
  loadAverage1m: z.number().nonnegative().nullable(),
  containersRunning: nonNegativeIntSchema.nullable(),
  siteCount: nonNegativeIntSchema,
});

export type NodeMetricPoint = z.infer<typeof nodeMetricPointSchema>;

export const nodeMetricSeriesSchema = z.object({
  nodeId: uuidSchema,
  from: isoDateTimeSchema,
  to: isoDateTimeSchema,
  /** Breite eines Eimers in Sekunden. */
  bucketSeconds: positiveIntSchema,
  /**
   * Die Punkte, aufsteigend nach Zeit.
   *
   * Leere Eimer werden **weggelassen** und nicht mit Nullen gefüllt: Eine 0 %
   * CPU-Last ist eine Aussage, „keine Messung" ist keine. Ein Diagramm, das
   * beides gleich zeichnet, macht aus einem ausgefallenen Agent einen
   * ruhigen Server.
   */
  points: z.array(nodeMetricPointSchema),
});

export type NodeMetricSeries = z.infer<typeof nodeMetricSeriesSchema>;
